import { useEffect, useState } from 'react';
import { Check, Loader2, Mail, MailX, ShieldAlert, ShieldCheck } from 'lucide-react';
import { ActionRowView } from '../../widget-native/viewir/components/actionrow';
import { api } from '../../../services/api';
import { useGmailConnect } from './mailHooks';
import { C, ROW_BOX, S, btn, clampLines } from './mailStyles';
import { providerCompany, providerName } from './mailUtils';

const BULLETS = [
  'Read email to build summaries and suggestions',
  'Nothing is sent, archived, or unsubscribed without your OK',
  'Disconnect anytime in Settings',
];

export function GmailConnectedRow({ provider, email, switching = false }) {
  return (
    <div style={ROW_BOX} data-testid="gmail-connected-row">
      <ActionRowView
        variant="card"
        iconStyle="badge"
        tone="#34C759"
        glyph={<Check size={16} color="#34C759" strokeWidth={2.5} aria-hidden="true" />}
        title={`${providerName(provider)} connected${switching ? ' · now in use' : ''}`}
        subtitle={email || ''}
      />
    </div>
  );
}

const BRAND = { gmail: { color: '#EA4335', tint: '#EA43351A' }, outlook: { color: '#0078D4', tint: '#0078D41A' } };

function minimalSpec(state, provider) {
  const name = providerName(provider);
  if (state === 'needs_more_access') {
    return { Icon: ShieldAlert, color: '#FF9F0A', tint: '#FF9F0A1F', text: `${name} needs more access to send`, action: 'Fix' };
  }
  if (state === 'disconnected') {
    return { Icon: MailX, color: '#FF453A', tint: '#FF453A1A', text: `${name} disconnected — report paused`, action: 'Reconnect' };
  }
  return { Icon: Mail, ...(BRAND[provider] || BRAND.gmail), text: `Connect ${name} to read your inbox`, action: 'Connect' };
}

const pillStyle = (busy) => ({
  height: 30, padding: '0 12px', borderRadius: 999, border: 'none', background: '#111111', color: '#fff',
  fontSize: 13, fontWeight: 600, fontFamily: C.font, cursor: busy ? 'default' : 'pointer', flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
});

function StatusLines({ phase, error, serverError, app }) {
  return (
    <>
      {serverError && phase === 'idle' && <p style={S.error}>{serverError}</p>}
      {error && <p style={S.error}>{error}</p>}
      {phase === 'timeout' && (
        <p style={S.warn}>We didn't see the connection finish. Try again once you've signed in.</p>
      )}
      {phase === 'waiting' && (
        <p style={S.sub}>Finish signing in with {providerCompany(app)} in the new tab — this updates automatically.</p>
      )}
    </>
  );
}

const inUsePill = {
  height: 30, padding: '0 12px', borderRadius: 999, border: `1px solid ${C.border}`, background: '#fff',
  color: C.textFaint, fontSize: 13, fontWeight: 600, fontFamily: C.font, cursor: 'default', flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
};

function useConnectedEmail(active, app) {
  const [found, setFound] = useState(null);
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    api.getMailConnection()
      .then((info) => {
        if (alive && typeof info?.email === 'string' && info.email && (!info.provider || info.provider === app)) {
          setFound(info.email);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [active, app]);
  return active ? found : null;
}

export function GmailConnectRow({
  state = 'not_connected', provider, providers = [], email, connectUrl, connectionId, serverError, onConnected,
  switching = false, currentProvider = null,
}) {
  const choice = !provider && providers.length > 1 && state === 'not_connected';
  const { phase, error, start, app } = useGmailConnect({ connectUrl, connectionId, onConnected, app: provider || 'gmail' });
  const connectedEmail = useConnectedEmail(phase === 'connected' && !email, app);
  if (phase === 'connected') {
    return <GmailConnectedRow provider={app} email={email || connectedEmail} switching={switching} />;
  }
  const spec = choice
    ? { Icon: Mail, color: '#111111', tint: '#1111110F', text: 'Connect your mailbox' }
    : minimalSpec(state, provider || 'gmail');
  const busy = phase === 'opening' || phase === 'waiting';
  const { Icon } = spec;
  if (!choice) {
    return (
      <div style={ROW_BOX} data-testid="gmail-connect-row" data-state={state}>
        <ActionRowView
          variant="card"
          tone={spec.color}
          glyph={<Icon size={16} color={spec.color} aria-hidden="true" />}
          title={spec.text}
          actionLabel={spec.action}
          actionBusy={busy}
          onAction={() => start()}
        >
          <StatusLines phase={phase} error={error} serverError={serverError} app={app} />
        </ActionRowView>
      </div>
    );
  }
  return (
    <div
      style={{ ...S.card, ...S.assistant, border: 'none', boxShadow: '0 1px 3px #1018281F', gap: 8 }}
      data-testid="gmail-connect-row"
      data-state={state}
      data-choice="true"
    >
      <div style={{ ...S.row, gap: 10, alignItems: 'flex-start' }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: spec.tint, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <Icon size={16} color={spec.color} aria-hidden="true" />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111111', lineHeight: 1.35, ...clampLines(2) }}>
            {spec.text}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {providers.map((option) => (option === currentProvider ? (
              <button key={option} type="button" disabled style={inUsePill} data-testid={`mail-connect-${option}`}>
                {providerName(option)} · In use
              </button>
            ) : (
              <button
                key={option}
                type="button"
                disabled={busy}
                onClick={() => start(option)}
                style={pillStyle(busy)}
                data-testid={`mail-connect-${option}`}
              >
                {busy && app === option && <Loader2 size={13} className="animate-spin" aria-hidden="true" />}
                {providerName(option)}
              </button>
            )))}
          </div>
        </div>
      </div>
      <StatusLines phase={phase} error={error} serverError={serverError} app={app} />
    </div>
  );
}

export function MailUnavailable() {
  return (
    <div style={{ ...S.card, padding: '10px 14px' }} data-testid="mail-unavailable">
      <p style={{ ...S.sub, color: C.textFaint }}>Not available</p>
    </div>
  );
}

export default function GmailConnectPanel({
  expired = false, disconnected = false, disconnectedSub, connectUrl, serverError, onConnected, bare = false, provider,
}) {
  const app = provider || 'gmail';
  const { phase, error, start } = useGmailConnect({ connectUrl, onConnected, app });
  const name = providerName(app);
  const company = providerCompany(app);

  if (phase === 'connected' && !disconnected) return <GmailConnectedRow provider={app} />;
  const reconnect = expired || disconnected;
  const title = disconnected ? `${name} disconnected` : expired ? `${name} access expired` : `Connect ${name}`;
  const sub = disconnected
    ? (disconnectedSub || 'Reconnect to see this digest again.')
    : expired ? 'Reconnect so we can keep sorting your inbox.' : 'One-time setup · takes 10 seconds';

  const busy = phase === 'opening' || phase === 'waiting';
  const buttonLabel = phase === 'opening'
    ? `Opening ${company}…`
    : phase === 'waiting'
      ? `Waiting for ${company}…`
      : reconnect ? `Reconnect ${name}` : `Connect ${name}`;

  return (
    <div style={bare ? { display: 'flex', flexDirection: 'column', gap: 12 } : S.card} data-testid="gmail-connect-panel">
      <div style={S.row}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: C.bgMuted, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Mail size={18} color={reconnect ? C.orange : C.text} aria-hidden="true" />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={S.title}>{title}</p>
          <p style={S.sub}>{sub}</p>
        </div>
      </div>
      {!reconnect && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {BULLETS.map((b) => (
            <li key={b} style={{ display: 'flex', gap: 8, fontSize: 13, color: C.text, lineHeight: 1.4 }}>
              <Check size={14} color={C.green} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {serverError && phase === 'idle' && <p style={S.error}>{serverError}</p>}
      {error && <p style={S.error}>{error}</p>}
      {phase === 'timeout' && (
        <p style={S.warn}>We didn't see the connection finish. Try again once you've signed in.</p>
      )}
      {phase === 'waiting' && (
        <p style={S.sub}>Finish signing in with {company} in the new tab — this updates automatically.</p>
      )}
      <button type="button" style={btn(S.btnPrimary, busy)} disabled={busy} onClick={start}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
          {buttonLabel}
        </span>
      </button>
      <p style={{ ...S.footnote, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <ShieldCheck size={12} aria-hidden="true" />
        Secured by {company} · we never see your password
      </p>
    </div>
  );
}
